import type {Theme} from '@emotion/react';

/**
 * Props interface for button color utility functions.
 * This interface defines the required theme object that contains color definitions
 * used by the button styling functions to generate appropriate CSS custom properties.
 * The theme object follows the Emotion theme structure and provides access to the
 * color palette used throughout the application.
 */
interface ButtonColorProps {
    /**
     * The Emotion theme object containing color definitions.
     * This theme provides access to all color tokens including primary, secondary,
     * background, and text colors used for styling button variants across different
     * themes (light/dark) and states (normal, hover, focus, disabled).
     */
    theme: Theme;
}

/**
 * Generates CSS custom properties for default button colors based on theme and variant.
 * This function creates the base color scheme for buttons in their normal state, handling
 * different theme modes (light/dark) and button variants (primary, secondary, outlined).
 * The returned CSS string defines custom properties for background, border, and font colors
 * that can be applied to button elements with appropriate data attributes.
 *
 * @param props       The configuration object for button colors.
 * @param props.theme The Emotion theme object containing color definitions used to generate appropriate color values for different button states and variants.
 * @returns A CSS template string containing custom property definitions for button colors with variant-specific overrides for light and dark themes.
 *
 * @example
 * ```tsx
 * const StyledButton = styled.button`
 *   ${getButtonColors}
 *   background-color: var(--bg-color);
 *   border-color: var(--border-color);
 *   color: var(--font-color);
 * `;
 * ```
 */
export const getButtonColors = ({theme}: ButtonColorProps) => `
    --bg-color: hsl(from ${theme.colors.primary} h s l);
    --border-color: hsl(from ${theme.colors.primary} h s l);
    --font-color: hsl(from ${theme.colors.textLight} h s l);

    &[data-variant=icon-only] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --border-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }

    &[data-theme=light][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --border-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }
    &[data-theme=dark][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.textPrimary} h s l);
        --border-color: hsl(from ${theme.colors.textPrimary} h s l);
        --font-color: hsl(from ${theme.colors.textLight} h s l);
    }
    &[data-theme=light][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l / 0);
        --border-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --font-color: hsl(from ${theme.colors.textLight} h s l);
    }
    &[data-theme=dark][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.textPrimary} h s l / 0);
        --border-color: hsl(from ${theme.colors.textPrimary} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }
`;

/**
 * Generates CSS custom properties for button hover colors based on theme and variant.
 * This function creates the color scheme for buttons when they are in a hover state, providing
 * visual feedback to users during mouse interaction. It handles different theme modes (light/dark)
 * and button variants (primary, secondary, outlined) with appropriate color transitions that maintain
 * accessibility and visual hierarchy while providing clear interactive feedback.
 *
 * @param props       The configuration object for button hover colors.
 * @param props.theme The Emotion theme object containing color definitions used to generate appropriate hover color values for different button states and variants.
 * @returns A CSS template string containing custom property definitions for button hover colors with variant-specific overrides for light and dark themes.
 *
 * @example
 * ```tsx
 * const StyledButton = styled.button`
 *   ${getButtonColors}
 *
 *   &:hover {
 *     ${getButtonHoverColors}
 *   }
 *
 *   background-color: var(--bg-color);
 *   border-color: var(--border-color);
 *   color: var(--font-color);
 * `;
 * ```
 */
export const getButtonHoverColors = ({theme}: ButtonColorProps) => `
    --bg-color: hsl(from ${theme.colors.primaryDark} h s l);
    --border-color: hsl(from ${theme.colors.primaryDark} h s l);
    --font-color: hsl(from ${theme.colors.textLight} h s l);

    &[data-variant=icon-only] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s calc(l - 5));
        --border-color: hsl(from ${theme.colors.backgroundLight} h s calc(l - 5));
        --font-color: hsl(from ${theme.colors.primary} h s l);
    }
    &[data-theme=light][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --border-color: hsl(from ${theme.colors.textPrimary} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }
    &[data-theme=dark][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.textPrimary} h s l);
        --border-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --font-color: hsl(from ${theme.colors.textLight} h s l);
    }
    &[data-theme=light][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l / 0.3);
        --border-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --font-color: hsl(from ${theme.colors.textLight} h s l);
    }
    &[data-theme=dark][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.textPrimary} h s l / 0.3);
        --border-color: hsl(from ${theme.colors.textPrimary} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }
`;

/**
 * Generates CSS custom properties for button focus colors based on theme and variant.
 * This function creates the color scheme for buttons when they receive keyboard focus or
 * programmatic focus, providing clear accessibility indicators for users navigating with
 * assistive technologies. It handles different theme modes (light/dark) and button variants
 * (primary, secondary, outlined) with enhanced border colors using the tertiary color to
 * ensure proper focus visibility and WCAG compliance for interactive elements.
 *
 * @param props       The configuration object for button focus colors.
 * @param props.theme The Emotion theme object containing color definitions used to generate appropriate focus color values with enhanced border visibility for accessibility compliance.
 * @returns A CSS template string containing custom property definitions for button focus colors with variant-specific overrides and tertiary border colors for enhanced focus visibility.
 *
 * @example
 * ```tsx
 * const StyledButton = styled.button`
 *   ${getButtonColors}
 *
 *   &:focus {
 *     ${getButtonFocusColors}
 *   }
 *
 *   background-color: var(--bg-color);
 *   border-color: var(--border-color);
 *   color: var(--font-color);
 * `;
 * ```
 */
export const getButtonFocusColors = ({theme}: ButtonColorProps) => `
    --bg-color: hsl(from ${theme.colors.primary} h s l);
    --border-color: hsl(from ${theme.colors.textPrimary} h s l);
    --font-color: hsl(from ${theme.colors.textLight} h s l);

    &[data-variant=icon-only] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s calc(l - 5));
        --border-color: hsl(from ${theme.colors.backgroundLight} h s calc(l - 5));
        --font-color: hsl(from ${theme.colors.primary} h s l);
    }
    &[data-theme=light][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l);
        --border-color: hsl(from ${theme.colors.tertiary} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }
    &[data-theme=dark][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.textPrimary} h s l);
        --border-color: hsl(from ${theme.colors.tertiary} h s l);
        --font-color: hsl(from ${theme.colors.textLight} h s l);
    }
    &[data-theme=light][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.backgroundLight} h s l / 0.3);
        --border-color: hsl(from ${theme.colors.tertiary} h s l);
        --font-color: hsl(from ${theme.colors.textLight} h s l);
    }
    &[data-theme=dark][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.textPrimary} h s l / 0.3);
        --border-color: hsl(from ${theme.colors.tertiary} h s l);
        --font-color: hsl(from ${theme.colors.textPrimary} h s l);
    }
`;

/**
 * Generates CSS custom properties for button disabled colors based on theme and variant.
 * This function creates the color scheme for buttons when they are in a disabled state, providing
 * clear visual indication that the button is not interactive and cannot be activated by user input.
 * It handles different theme modes (light/dark) and button variants (primary, secondary, outlined)
 * by applying neutral colors that maintain accessibility while clearly communicating the disabled
 * state to users, including those using assistive technologies.
 *
 * @param props       The configuration object for button disabled colors.
 * @param props.theme The Emotion theme object containing color definitions used to generate appropriate disabled color values with neutral tones for non-interactive button states.
 * @returns A CSS template string containing custom property definitions for button disabled colors with variant-specific overrides using neutral color palette for consistent disabled appearance.
 *
 * @example
 * ```tsx
 * const StyledButton = styled.button`
 *   ${getButtonColors}
 *
 *   &:disabled {
 *     ${getButtonDisabledColors}
 *   }
 *
 *   background-color: var(--bg-color);
 *   border-color: var(--border-color);
 *   color: var(--font-color);
 * `;
 * ```
 */
export const getButtonDisabledColors = ({theme}: ButtonColorProps) => `
    --bg-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
    --border-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
    --font-color: hsl(from ${theme.colors.textNeutral} h s l);

    &[data-variant=icon-only] {
        --bg-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --border-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --font-color: hsl(from ${theme.colors.textNeutral} h s l);
    }
    &[data-theme=light][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --border-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --font-color: hsl(from ${theme.colors.textNeutral} h s l);
    }
    &[data-theme=dark][data-variant=secondary] {
        --bg-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --border-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --font-color: hsl(from ${theme.colors.textNeutral} h s l);
    }
    &[data-theme=light][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --border-color: hsl(from ${theme.colors.textNeutral} h s l);
        --font-color: hsl(from ${theme.colors.textNeutral} h s l);
    }
    &[data-theme=dark][data-variant=outlined] {
        --bg-color: hsl(from ${theme.colors.backgroundNeutral} h s l);
        --border-color: hsl(from ${theme.colors.textNeutral} h s l);
        --font-color: hsl(from ${theme.colors.textNeutral} h s l);
    }
`;